import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { JobDocument } from "../job-document/job-document.component";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { JobDocumentTypeEnum } from "../job-document/job-document.types";
import { CreateJobDocumentFromTemplateDialog } from "./create-job-document-from-template-dialog.component";
import { JobDocumentSidebar } from "./job-document-sidebar";
import { Tables } from "@/lib/supabase";
import { FileViewerModal } from "@/components/src/file-viewer";

interface JobDocumentTabsProps {
	onOpenVendorDialog: () => void;
	childJobChannels: Tables<"message_channels">[] | null;
	onChildJobChannelClick: (channelId: string) => void;
}

export const JobDocumentTabs: React.FC<JobDocumentTabsProps> = ({
	onOpenVendorDialog,
	childJobChannels,
	onChildJobChannelClick,
}) => {
	const {
		jobDocumentsObj,
		createJobDocument,
		jobId,
		clientId,
		selectedDocumentId,
		setSelectedDocumentId,
	} = useJobStore((state) => ({
		jobDocumentsObj: state.jobDocuments,
		createJobDocument: state.createJobDocument,
		jobId: state.job?.id,
		clientId: state.job?.client_id,
		selectedDocumentId: state.selectedDocumentId,
		setSelectedDocumentId: state.setSelectedDocumentId,
	}));

	const sortedDocuments = useMemo(() => {
		return Object.values(jobDocumentsObj).sort((a, b) => {
			if (!a.date && !b.date) return 0;
			if (!a.date) return -1;
			if (!b.date) return 1;
			if (a.date == b.date) {
				// if dates are equal, sort by created_at
				if (
					!a.created_at ||
					!b.created_at ||
					a.created_at == b.created_at
				)
					return 0;
				return a.created_at < b.created_at ? 1 : -1;
			}
			return new Date(b.date).getTime() - new Date(a.date).getTime();
		});
	}, [jobDocumentsObj]);

	const [templateTypeDialog, setTemplateTypeDialog] =
		useState<JobDocumentTypeEnum | null>(null);
	const [sidebarFileViewerOpen, setSidebarFileViewerOpen] = useState(false);
	const [sidebarSelectedFileId, setSidebarSelectedFileId] = useState<
		string | null
	>(null);

	const allFileIds = useMemo(() => {
		const ids: string[] = [];
		Object.values(jobDocumentsObj).forEach((doc) => {
			doc.files_with_shares?.forEach((file) => {
				if (file.id) ids.push(file.id);
			});
		});
		return ids;
	}, [jobDocumentsObj]);

	useEffect(() => {
		if (sortedDocuments.length > 0) {
			setSelectedDocumentId(sortedDocuments[0].id);
		} else {
			setSelectedDocumentId(null);
		}
	}, [jobId]);

	const handleSelectDocument = useCallback((documentId: number | null) => {
		setSelectedDocumentId(documentId);
	}, []);

	const isCreatingDocumentRef = useRef(false);

	const handleCreateDocument = async (type: JobDocumentTypeEnum) => {
		if (isCreatingDocumentRef.current) return;
		isCreatingDocumentRef.current = true;
		try {
			const jobDocumentId = await createJobDocument(type);
			if (jobDocumentId) {
				setSelectedDocumentId(jobDocumentId);
			}
		} finally {
			isCreatingDocumentRef.current = false;
		}
	};

	const handleCreateFromTemplate = (type: JobDocumentTypeEnum) => {
		setTemplateTypeDialog(type);
	};

	const handleSidebarFileClick = useCallback((fileId: string) => {
		setSidebarSelectedFileId(fileId);
		setSidebarFileViewerOpen(true);
	}, []);

	const handleDocumentCreatedFromTemplate = useCallback(
		(documentId: number) => {
			setSelectedDocumentId(documentId);
		},
		[]
	);

	const selectedDocument = selectedDocumentId
		? jobDocumentsObj[selectedDocumentId]
		: null;

	return (
		<div className="flex h-full w-full">
			<JobDocumentSidebar
				sortedDocuments={sortedDocuments}
				childJobChannels={childJobChannels}
				selectedDocumentId={selectedDocumentId}
				onSelectDocument={handleSelectDocument}
				onOpenVendorDialog={onOpenVendorDialog}
				onFileClick={handleSidebarFileClick}
				onCreateDocument={handleCreateDocument}
				onCreateFromTemplate={handleCreateFromTemplate}
				onChildJobChannelClick={onChildJobChannelClick}
			/>

			<div className="flex-1 flex flex-col min-w-0 overflow-hidden">
				{selectedDocument ? (
					<div className="flex-1 overflow-auto">
						<JobDocument
							key={selectedDocumentId}
							jobDocument={selectedDocument}
							onDocumentDuplicated={(documentId) =>
								setSelectedDocumentId(documentId)
							}
						/>
					</div>
				) : (
					<div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
						Kein Dokument ausgewählt
					</div>
				)}
			</div>

			<CreateJobDocumentFromTemplateDialog
				open={templateTypeDialog !== null}
				setOpen={(open) => {
					if (!open) {
						setTemplateTypeDialog(null);
					}
				}}
				templateType={
					templateTypeDialog ?? JobDocumentTypeEnum.Quotation
				}
				onDocumentCreated={handleDocumentCreatedFromTemplate}
			/>

			{allFileIds.length > 0 && (
				<FileViewerModal
					fileIds={allFileIds}
					isOpen={sidebarFileViewerOpen}
					onClose={() => {
						setSidebarFileViewerOpen(false);
						setSidebarSelectedFileId(null);
					}}
					defaultFileId={sidebarSelectedFileId || undefined}
					metadata={{
						jobId: jobId ?? undefined,
						clientId: clientId ?? undefined,
					}}
				/>
			)}
		</div>
	);
};
